import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import logoLight from '../assets/images/logo-light.png';
import logoDark from '../assets/images/logo-dark.png';
import PrivacyDialog from './PrivacyDialog';

interface AppFooterProps {
  apiStatus: boolean;
  dbStatus: boolean;
  /**
   * Versione ridotta a una riga, per le pagine interne. Le colonne con logo,
   * descrizione e contatti servono a chi arriva da fuori, mentre dentro l'app
   * su telefono occupavano piu' spazio del contenuto della pagina. Restano
   * raggiungibili aiuto, informativa e termini, e i pallini di stato.
   */
  compatto?: boolean;
}

const StatusIndicator: React.FC<{ status: boolean; label: string }> = ({ status, label }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={`${label}: ${status ? t('connected') : t('disconnected')}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: status ? '#4D9C7E' : '#E74C3C',
            boxShadow: status
              ? '0 0 6px rgba(77, 156, 126, 0.5)'
              : '0 0 6px rgba(231, 76, 60, 0.5)',
            transition: 'all 0.3s ease',
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const TERMS_SECTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
const HOW_IT_WORKS_STEPS = [1, 2, 3, 4, 5] as const;
const FAQ_ITEMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const AppFooter: React.FC<AppFooterProps> = ({ apiStatus, dbStatus, compatto = false }) => {
  const { t } = useTranslation();
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmall = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const logoSrc = mode === 'dark' ? logoDark : logoLight;
  const isDark = mode === 'dark';

  const footerBg = isDark ? '#0d1117' : '#141a1f';
  const footerBorder = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.06)';

  const linkSx = {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: 44,
    transition: 'color 0.2s',
    textDecoration: 'none',
    '&:hover': { color: '#3BC0B7' },
  } as const;

  // I link erano alti quanto il testo (una ventina di pixel): un'area cosi'
  // bassa si centra male col dito. L'altezza minima li rende tappabili senza
  // cambiare l'aspetto del testo.
  const bottomLinkSx = {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '0.8125rem',
    transition: 'color 0.2s',
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    // le voci corte ("FAQ") restavano larghe 25px: alte ma sottili
    minWidth: 44,
    '&:hover': { color: '#3BC0B7' },
  } as const;

  const colTitleSx = {
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9375rem',
    mb: 2.5,
  } as const;

  return (
    <>
      <Box
        component="footer"
        sx={{
          backgroundColor: footerBg,
          color: 'rgba(255,255,255,0.5)',
          pt: compatto ? 1 : { xs: 5, md: 8 },
          pb: compatto ? 1 : { xs: 3, md: 4 },
          px: { xs: 3, md: 6 },
        }}
      >
        {/* Grid */}
        {!compatto && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? isSmall
                ? '1fr'
                : '1fr 1fr'
              : '2fr 1fr 1fr 1fr',
            gap: { xs: '36px', md: '48px' },
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Brand */}
          <Box>
            <Box
              component="img"
              src={logoSrc}
              alt="SkyJump"
              sx={{ height: 32, mb: 2 }}
            />
            <Typography
              sx={{
                fontSize: '0.9375rem',
                lineHeight: 1.6,
                maxWidth: 300,
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              {t('footer.brand_description')}
            </Typography>
          </Box>

          {/* Product */}
          <Box>
            <Typography sx={colTitleSx}>
              {t('footer.product')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link component="button" onClick={() => setHowItWorksOpen(true)} sx={linkSx}>{t('footer.how_it_works')}</Link>
              <Link component="button" onClick={() => setFaqOpen(true)} sx={linkSx}>{t('footer.faq')}</Link>
            </Box>
          </Box>

          {/* Resources */}
          <Box>
            <Typography sx={colTitleSx}>
              {t('footer.resources')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link href="/public/docs" target="_blank" rel="noopener" sx={linkSx}>{t('footer.documentation')}</Link>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <StatusIndicator status={apiStatus} label={t('api_label')} />
                <StatusIndicator status={dbStatus} label={t('db_label')} />
              </Box>
            </Box>
          </Box>

          {/* Contacts */}
          <Box>
            <Typography sx={colTitleSx}>
              {t('footer.contacts')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link href="mailto:info@skyjump.cloud" sx={linkSx}>
                <EmailIcon sx={{ fontSize: 16 }} /> info@skyjump.cloud
              </Link>
              {/* Il telefono era un segnaposto ("+39 XXX XXX XXXX", che portava
                  a tel:+39000000000): meglio nessun numero che un numero finto
                  ma cliccabile. Rimettere qui il recapito vero quando c'e'. */}
            </Box>
          </Box>
        </Box>
        )}

        {/* Bottom bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmall ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: compatto ? 0 : 3,
            borderTop: compatto ? 'none' : `1px solid ${footerBorder}`,
            gap: isSmall ? 1 : 0,
            flexWrap: 'wrap',
            textAlign: isSmall ? 'center' : 'left',
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.5)' }}>
            {t('footer.copyright')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: compatto ? 2 : 3,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: isSmall ? 'center' : 'flex-end',
            }}
          >
            {/* Nella riga ridotta l'aiuto e lo stato restano a portata: sono
                cose che serve consultare mentre si lavora, non pubblicita'. */}
            {compatto && (
              <>
                <Link component="button" onClick={() => setHowItWorksOpen(true)} sx={bottomLinkSx}>
                  {t('footer.how_it_works')}
                </Link>
                <Link component="button" onClick={() => setFaqOpen(true)} sx={bottomLinkSx}>
                  {t('footer.faq')}
                </Link>
                <Link href="/public/docs" target="_blank" rel="noopener" sx={bottomLinkSx}>
                  {t('footer.documentation')}
                </Link>
                {/* i due pallini restano insieme: andando a capo separati si
                    leggevano come due cose diverse */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 44 }}>
                  <StatusIndicator status={apiStatus} label={t('api_label')} />
                  <StatusIndicator status={dbStatus} label={t('db_label')} />
                </Box>
              </>
            )}
            <Link
              component="button"
              onClick={() => setPrivacyOpen(true)}
              sx={bottomLinkSx}
            >
              {t('footer.privacy_policy')}
            </Link>
            <Link
              component="button"
              onClick={() => setTermsOpen(true)}
              sx={bottomLinkSx}
            >
              {t('footer.terms_of_service')}
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Privacy Dialog */}
      <PrivacyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />

      {/* Terms of Service Dialog */}
      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('terms.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('terms.last_updated')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('terms.intro')}
          </Typography>
          {TERMS_SECTIONS.map((n) => (
            <Box key={n} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t(`terms.section${n}_title`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`terms.section${n}_text`)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOpen(false)} startIcon={<CloseIcon />}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* How It Works Dialog */}
      <Dialog open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('how_it_works.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('how_it_works.subtitle')}
          </Typography>
          {HOW_IT_WORKS_STEPS.map((n) => (
            <Box
              key={n}
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(`how_it_works.step${n}_title`)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="primary">
                    {t(`how_it_works.step${n}_time`)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t(`how_it_works.step${n}_text`)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHowItWorksOpen(false)} startIcon={<CloseIcon />}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqOpen} onClose={() => setFaqOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('faq_page.title')}</DialogTitle>
        <DialogContent dividers>
          {FAQ_ITEMS.map((n) => (
            <Box key={n} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t(`faq_page.q${n}`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`faq_page.a${n}`)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqOpen(false)} startIcon={<CloseIcon />}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppFooter;
